import React, { useEffect } from 'react';
import { useForm, FormContextValues } from 'react-hook-form';
import { Platform, StyleSheet, View, Text } from 'react-native';
import { useMediaQuery } from 'react-responsive';

import {
  ADDRESS_FORM_FIELD_LABELS,
  ADDRESS_FORM_FIELD_NAMES,
  ADDRESS_FORM_FIELD_PLACEHOLDERS,
  AddressProps,
} from 'shared/constants/ekyc';
import prefectures from 'shared/constants/prefectures';
import Button from 'shared/modules/common/components/atoms/Button';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import Picker from 'shared/modules/common/components/molecules/Picker';
import validator, { Rule } from 'shared/utils/validator';

type Props = {
  onNextButtonPress: () => void;
  onReturnButtonPress: () => void;
  form: FormContextValues<AddressProps>;
  onFocus?: () => void;
  onBlur?: () => void;
  onFieldBlur?: (field: 'zip' | 'state' | 'city' | 'line2' | 'line1') => void;
};

const Address = (props: Props) => {
  const {
    onNextButtonPress,
    onReturnButtonPress,
    form,
    onFocus,
    onBlur,
    onFieldBlur,
  } = props;
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  useEffect(() => {
    onFocus?.();

    return () => onBlur?.();
  }, []);

  return (
    <View style={styles.root}>
      <View style={styles.panel}>
        <Text
          style={styles.notifTitle}
        >{`自動入力された住所をご確認ください`}</Text>
        <Text style={styles.notifBody}>
          {`身分証明書と完全に一致する情報を入力してください。\n情報が一致しない場合、本人確認ができません。`}
        </Text>
      </View>
      <View style={[styles.panel, { paddingBottom: 0 }]}>
        <View
          style={styles.form}
          {...Platform.select({ web: { accessibilityRole: 'form' } })}
        >
          {ADDRESS_FORM_FIELD_NAMES.map((field, index) => {
            if (field === 'state') {
              return (
                <Picker
                  key={index}
                  value={form.getValues(field)}
                  label={ADDRESS_FORM_FIELD_LABELS[index]}
                  placeholder={ADDRESS_FORM_FIELD_PLACEHOLDERS[index]}
                  options={prefectures.map((place) => ({
                    label: place,
                    value: place,
                  }))}
                  selectButtonText="保存する"
                  onSelect={(text) => form.setValue(field, text)}
                  onBlur={() => {
                    onFieldBlur?.(field);
                    form.triggerValidation(field);
                  }}
                  message={(form.errors[field]?.message as string) || ''}
                  state={form.errors[field]?.message ? 'error' : undefined}
                  style={styles.textInput}
                  dialogStyle={styles.pickerDialog}
                  showAsDialog={isBigScreen}
                  id={`ekyc-address-form-${field}-picker`}
                />
              );
            }

            return (
              <TextInput
                key={index}
                value={form.getValues(field)}
                label={ADDRESS_FORM_FIELD_LABELS[index]}
                placeholder={ADDRESS_FORM_FIELD_PLACEHOLDERS[index]}
                onChangeText={(text) => form.setValue(field, text)}
                onBlur={() => {
                  onFieldBlur?.(field);
                  form.triggerValidation(field);
                }}
                message={(form.errors[field]?.message as string) || ''}
                state={form.errors[field]?.message ? 'error' : undefined}
                style={styles.textInput}
                id={`ekyc-address-form-${field}-field`}
              />
            );
          })}
        </View>
      </View>
      <View
        style={[
          styles.buttonContainer,
          isBigScreen && styles.buttonContainerBigScreen,
        ]}
      >
        <Button
          title="次へ"
          onPress={async () => {
            const isValid = await form.triggerValidation();

            if (isValid) {
              onNextButtonPress();
            }
          }}
          disabled={!form.formState.dirty}
          id="ekyc-address-form-next-button"
        />
        {isBigScreen && (
          <Button
            onPress={onReturnButtonPress}
            title="戻る"
            variant="outlined"
            style={styles.returnButton}
            id="ekyc-address-form-return-button"
          />
        )}
      </View>
    </View>
  );
};

export const useFormAddressWithValidations = (props: AddressProps) => {
  const form = useForm<AddressProps>({
    defaultValues: props,
  });

  const basicRules: Rule[] = [
    'mustNotBeEmptyOrSpace',
    'mustNotContainSpace',
    'mustNotContainNonPrintableChars',
  ];

  const zipRules: Rule[] = [...basicRules, 'mustBeZipCode'];

  const line1Rules: Rule[] = [basicRules[2]];

  const validatorRule = (fieldName: string) => {
    let rules = basicRules;

    if (fieldName === ADDRESS_FORM_FIELD_NAMES[4]) {
      // line1
      rules = line1Rules;
    }

    if (fieldName === ADDRESS_FORM_FIELD_NAMES[0]) {
      // zip
      rules = zipRules;
    }

    return validator.getRules(rules);
  };

  useEffect(() => {
    ADDRESS_FORM_FIELD_NAMES.forEach((name) => {
      form.register(
        {
          name,
        },
        {
          validate: validatorRule(name),
        }
      );
    });
  }, [form.register]);

  form.watch();

  return form;
};

const styles = StyleSheet.create({
  root: {
    flexDirection: 'column',
    flexGrow: 1,
  },
  panel: {
    padding: 24,
    borderRadius: 16,
    backgroundColor: '#fff',
    marginVertical: 12,
  },
  notifTitle: {
    fontSize: 14,
    color: '#FF2751',
    fontWeight: '600',
    marginBottom: 16,
  },
  notifBody: {
    fontSize: 12,
    color: '#1C1C1C',
  },
  form: {
    flex: 1,
    justifyContent: 'space-between',
  },
  textInput: {
    marginBottom: 24,
  },
  pickerDialog: {
    width: 424,
  },
  buttonContainer: {
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingTop: 12,
  },
  buttonContainerBigScreen: {
    paddingTop: 48,
  },
  returnButton: {
    marginTop: 18,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
});

export default Address;
